import React from 'react';
import { Download, MapPin, Award, Gift, IceCream, Map, Trophy, Compass } from 'lucide-react';
import '../styles/HowItWorks.css';

const HowItWorks = () => {
  const steps = [
    {
      icon: <Download size={48} />,
      number: "01",
      title: "Töltsd le az alkalmazást",
      description: "Kerésd meg a 'Tó Túra by Szürkebarát' alkalmazást az App Store-ban vagy a Google Play áruházban, és telepítsd az eszközödre."
    },
    {
      icon: <MapPin size={48} />,
      number: "02",
      title: "Válassz egy tavat",
      description: "Válaszd ki a Tisza-tavat, a Balatont vagy a Velencei-tavat, és indítsd el a túrát. Az alkalmazás végigvezet a teljes körön."
    },
    {
      icon: <Award size={48} />,
      number: "03",
      title: "Teljesítsd a kihívásokat",
      description: "Kerékpározz, fuss vagy csak sétálj a tó körül, látogasd meg a kijelölt pontokat, és teljesítsd a kiválasztott tó szakaszát."
    },
    {
      icon: <Gift size={48} />,
      number: "04",
      title: "Szerezd meg a jutalmad",
      description: "A túra teljesítése után vásárold meg a jutalom csomagot, amely tartalmaz egy személyre szabott digitális oklevelet és egy fagylalt kupont."
    }
  ];

  return (
    <section className="how-it-works-page" aria-label="Hogyan működik a Tó Túra alkalmazás">
      <div className="how-it-works-container">
        <header className="how-it-works-header">
          <h1 className="page-title">Hogyan működik a Tó Túra?</h1>
          <p className="page-subtitle">
            Egyszerű lépések a Balaton, Tisza-tó és Velencei-tó körüli kalandhoz és a jutalmakhoz
          </p>
        </header>

        <div className="steps-grid">
          {steps.map((step, index) => (
            <div key={index} className="step-card">
              <div className="step-number">{step.number}</div>
              <div className="step-icon">
                {step.icon}
              </div>
              <h3 className="step-title">{step.title}</h3>
              <p className="step-description">{step.description}</p>
            </div>
          ))}
        </div>

        <div className="certificates-section">
          <h2 className="certificates-title">Digitális Okleveleink</h2>
          <p className="certificates-subtitle">
            Esztétikus, személyre szabott digitális oklevél – örök emlék a teljesítményedről
          </p>
          <div className="certificates-grid">
            <div className="certificate-item">
              <div className="certificate-frame">
                <img 
                  src="/images/certificates/certificate_tisza.png" 
                  alt="Tisza-tó oklevél minta" 
                />
              </div>
              <div className="certificate-caption">Tisza-tó oklevél</div>
            </div>
            <div className="certificate-item">
              <div className="certificate-frame">
                <img 
                  src="/images/certificates/certificate_balaton.png" 
                  alt="Balaton oklevél minta" 
                />
              </div>
              <div className="certificate-caption">Balaton oklevél</div>
            </div>
            <div className="certificate-item">
              <div className="certificate-frame">
                <img 
                  src="/images/certificates/certificate_velencei.png" 
                  alt="Velencei-tó oklevél minta" 
                />
              </div>
              <div className="certificate-caption">Velencei-tó oklevél</div>
            </div>
          </div>
        </div>

        <div className="locations-section">
          <h2 className="locations-title">Fagylalt Átvételi Helyek</h2>
          <p className="locations-subtitle">
            Használd a QR kódod ezeken a helyszíneken az ingyenes fagylaltokért
          </p>
          <div className="locations-grid">
            <div className="location-card">
              <div className="location-icon">
                <IceCream size={64} strokeWidth={1.5} />
              </div>
              <h3 className="location-name">Budapest</h3>
              <p className="location-address">Bajcsy Zsilinszky 49.</p>
              <p className="location-country">Magyarország</p>
            </div>
            <div className="location-card">
              <div className="location-icon">
                <IceCream size={64} strokeWidth={1.5} />
              </div>
              <h3 className="location-name">Eger</h3>
              <p className="location-address">Dobó tér 6.</p>
              <p className="location-country">Magyarország</p>
            </div>
          </div>
        </div>

        <div className="features-section">
          <h2 className="features-title">Miért válaszd a Tó Túrát?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <Map size={36} strokeWidth={1.5} />
              </div>
              <h4>Interaktív térkép</h4>
              <p>Navigálj könnyedén az alkalmazás beépített térképével és GPS követésével online vagy offline.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Trophy size={36} strokeWidth={1.5} />
              </div>
              <h4>Digitális oklevél</h4>
              <p>Szerezz személyre szabott, letölthető oklevelet a teljesítésedről.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <IceCream size={36} strokeWidth={1.5} />
              </div>
              <h4>Azonnali jutalmak</h4>
              <p>Szerezd meg a QR kódot a fagylaltokért közvetlenül az alkalmazásban.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <Compass size={36} strokeWidth={1.5} />
              </div>
              <h4>Érdekes helyek</h4>
              <p>Fedezz fel érdekes éttermeket és látnivalókat az útvonal mentén.</p>
            </div>
          </div>
        </div>

        <div className="cta-section">
          <h2 className="cta-title">Készen állsz az indulásra?</h2>
          <p className="cta-description">
            Töltsd le a Tó Túra by Szürkebarát alkalmazást, és kezdd el felfedezni a Balatont, Tisza-tavat és Velencei-tavat még ma!
          </p>
          <div className="cta-buttons">
            <a 
              href="https://apps.apple.com/us/app/t%C3%B3-t%C3%BAra-by-sz%C3%BCrkebar%C3%A1t/id6752615113" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="cta-button apple"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.94 5.19A4.38 4.38 0 0 0 16 2a4.44 4.44 0 0 0-3 1.52 4.17 4.17 0 0 0-1 3.09 3.69 3.69 0 0 0 2.94-1.42m2.52 7.44a4.51 4.51 0 0 1 2.16-3.81 4.66 4.66 0 0 0-3.66-2c-1.56-.16-3 .91-3.83.91s-2-.89-3.3-.87a4.92 4.92 0 0 0-4.14 2.53C2.93 12.45 4.24 17 6 19.47c.8 1.21 1.8 2.58 3.12 2.53s1.75-.82 3.28-.82 2 .82 3.3.79 2.22-1.24 3.06-2.45a11 11 0 0 0 1.38-2.85 4.41 4.41 0 0 1-2.68-4.04"/>
              </svg>
              App Store
            </a>
            <a 
              href="https://play.google.com/store/apps/details?id=com.szurkebarat.toturak&pcampaignid=web_share" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="cta-button google"
            >
              <svg width="24" height="24" viewBox="-6.5 -0.016 100.001 100.001" xmlns="http://www.w3.org/2000/svg">
                <path d="m49.997 47.153 12.778-12.779C39.436 20.552 4.949.274 3-.016L2.844 0zM.016 2.829 0 2.984v94l.015.149 47.153-47.152zm49.987 49.987L2.852 99.968l.148.017c1.958-.312 36.439-20.58 59.775-34.396zM84 46.984s-7.372-4.398-17.665-10.5L52.832 49.988l13.497 13.497C76.625 57.382 84 52.984 84 52.984c1.607-.844 3-1.343 3-3 0-1.656-1.584-2.225-3-3"/>
              </svg>
              Google Play
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;

